<template>
  <div class="fixed inset-0 z-[100] grid place-items-center px-4 sm:px-6" role="dialog" aria-modal="true"
    aria-labelledby="auth-title">
    <div class="absolute inset-0 bg-black/70 backdrop-blur-xl" @click="closeModal" />

    <div ref="card" class="relative w-full max-w-5xl grid md:grid-cols-[1.1fr_1fr] overflow-hidden rounded-[28px]
             bg-white dark:bg-zinc-950 border border-black/10 dark:border-white/10
             shadow-[0_30px_120px_rgba(15,23,42,0.75)]">
      <div class="relative flex flex-col px-6 py-7 sm:px-8 sm:py-9 lg:px-10 lg:py-10">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <div class="h-11 w-11 rounded-2xl grid place-items-center border border-zinc-200/80 bg-white
           dark:border-zinc-700/70 dark:bg-zinc-900">
              <img src="@/assets/16.png" alt="NexGridCo" class="h-9 w-9 object-contain rounded-xl block dark:hidden" />
              <img src="@/assets/1.png" alt="NexGridCo" class="h-9 w-9 object-contain rounded-xl hidden dark:block" />
            </div>

            <div class="space-y-0.5">
              <p class="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
                NexGridCo
              </p>
              <p class="text-sm font-medium text-zinc-900 dark:text-zinc-50">
                Palette Studio Access
              </p>
            </div>
          </div>
          <div v-if="mode === 'login' || mode === 'signup'" class="hidden sm:inline-flex rounded-full bg-zinc-100 text-xs p-1
                   dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-800">
            <button type="button" @click="mode = 'login'; error = ''; info = ''" :class="[
              'px-3.5 py-1.5 rounded-full font-medium transition text-xs',
              mode === 'login'
                ? 'bg-white shadow-sm text-zinc-900 dark:bg-zinc-50 dark:text-zinc-950'
                : 'text-zinc-500 dark:text-zinc-400'
            ]">
              Sign in
            </button>
            <button type="button" @click="mode = 'signup'; error = ''; info = ''" :class="[
              'px-3.5 py-1.5 rounded-full font-medium transition text-xs',
              mode === 'signup'
                ? 'bg-white shadow-sm text-zinc-900 dark:bg-zinc-50 dark:text-zinc-950'
                : 'text-zinc-500 dark:text-zinc-400'
            ]">
              Create account
            </button>
          </div>
        </div>

        <div class="mt-8">
          <h1 id="auth-title"
            class="text-[1.9rem] sm:text-[2.1rem] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            {{ title }}
          </h1>
          <p class="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
            {{ subtitle }}
          </p>
        </div>

        <div class="mt-8 grid gap-4">
          <form v-if="mode === 'login'" @submit.prevent="submitLogin" class="grid gap-4">
            <div>
              <label for="email"
                class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                Email
              </label>
              <input v-model="email" id="email" type="email" autocomplete="email" required class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                       text-zinc-900 placeholder-zinc-400 outline-none transition
                       focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                       dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                       caret-sky-500 selection:bg-sky-500/15" />
            </div>
            <div>
              <label for="password"
                class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                Password
              </label>
              <input v-model="password" :type="showPwd ? 'text' : 'password'" id="password"
                autocomplete="current-password" required class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                       text-zinc-900 placeholder-zinc-400 outline-none transition
                       focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                       dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                       caret-sky-500 selection:bg-sky-500/15" />
            </div>

            <div class="flex items-center justify-between gap-3">
              <label class="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                <input v-model="showPwd" type="checkbox"
                  class="h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-600" />
                Show password
              </label>
              <button type="button" @click="mode = 'forgot'; error = ''; info = ''"
                class="text-xs font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300">
                Forgot password?
              </button>
            </div>

            <div v-if="error" class="rounded-xl border px-3 py-2 text-xs sm:text-sm"
              :class="isDark ? 'border-red-900/50 bg-red-950/60 text-red-300' : 'border-red-200/80 bg-red-50/90 text-red-700'">
              {{ error }}
            </div>

            <button type="submit" :disabled="loading" class="mt-2 w-full inline-flex items-center justify-center rounded-full px-4 py-2.5
                     text-sm font-semibold text-white shadow-lg shadow-sky-500/30
                     bg-sky-600 hover:bg-sky-700 active:scale-[.98] disabled:opacity-60 disabled:cursor-not-allowed">
              {{ loading ? 'Signing in…' : 'Sign in' }}
            </button>
          </form>

          <form v-else-if="mode === 'signup'" @submit.prevent="submitSignup" class="grid gap-4">
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="sm:col-span-2">
                <label for="name"
                  class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Name
                </label>
                <input v-model="name" id="name" type="text" required class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                         text-zinc-900 placeholder-zinc-400 outline-none transition
                         focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                         dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                         caret-sky-500 selection:bg-sky-500/15" />
              </div>
              <div class="sm:col-span-2">
                <label for="email2"
                  class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                  Email
                </label>
                <input v-model="email" id="email2" type="email" autocomplete="email" required class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                         text-zinc-900 placeholder-zinc-400 outline-none transition
                         focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                         dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                         caret-sky-500 selection:bg-sky-500/15" />
              </div>
            </div>

            <div>
              <label for="password2"
                class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                Password
              </label>
              <input v-model="password" :type="showPwd ? 'text' : 'password'" id="password2" autocomplete="new-password"
                required minlength="6" class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                       text-zinc-900 placeholder-zinc-400 outline-none transition
                       focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                       dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                       caret-sky-500 selection:bg-sky-500/15" />
            </div>

            <label class="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
              <input v-model="showPwd" type="checkbox"
                class="h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-600" />
              Show password
            </label>

            <div v-if="error" class="rounded-xl border px-3 py-2 text-xs sm:text-sm"
              :class="isDark ? 'border-red-900/50 bg-red-950/60 text-red-300' : 'border-red-200/80 bg-red-50/90 text-red-700'">
              {{ error }}
            </div>

            <button type="submit" :disabled="loading" class="mt-2 w-full inline-flex items-center justify-center rounded-full px-4 py-2.5
                     text-sm font-semibold text-white shadow-lg shadow-sky-500/30
                     bg-sky-600 hover:bg-sky-700 active:scale-[.98] disabled:opacity-60 disabled:cursor-not-allowed">
              {{ loading ? 'Creating…' : 'Create account' }}
            </button>
          </form>

          <form v-else-if="mode === 'forgot'" @submit.prevent="submitForgot" class="grid gap-4">
            <div>
              <label for="email3"
                class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                Your email
              </label>
              <input v-model="email" id="email3" type="email" required class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                       text-zinc-900 placeholder-zinc-400 outline-none transition
                       focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                       dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                       caret-sky-500 selection:bg-sky-500/15" />
            </div>

            <div v-if="info" class="rounded-xl border px-3 py-2 text-xs sm:text-sm"
              :class="isDark ? 'border-emerald-900/50 bg-emerald-950/60 text-emerald-300' : 'border-emerald-200/80 bg-emerald-50/90 text-emerald-700'">
              {{ info }}
            </div>

            <div v-if="error" class="rounded-xl border px-3 py-2 text-xs sm:text-sm"
              :class="isDark ? 'border-red-900/50 bg-red-950/60 text-red-300' : 'border-red-200/80 bg-red-50/90 text-red-700'">
              {{ error }}
            </div>

            <div class="flex items-center gap-2">
              <button type="submit" :disabled="loading" class="flex-1 inline-flex items-center justify-center rounded-full px-4 py-2.5
                       text-sm font-semibold text-white shadow-md
                       bg-zinc-900 hover:bg-black active:scale-[.98]
                       disabled:opacity-60 disabled:cursor-not-allowed
                       dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100">
                {{ loading ? 'Sending…' : 'Send reset link' }}
              </button>
              <button type="button" @click="mode = 'login'; error = ''; info = ''" class="rounded-full px-4 py-2.5 text-sm font-semibold
                       text-zinc-700 hover:bg-zinc-100
                       dark:text-zinc-300 dark:hover:bg-zinc-800/80">
                Back
              </button>
            </div>
          </form>

          <form v-else-if="mode === 'reset'" @submit.prevent="submitReset" class="grid gap-4">
            <div>
              <label class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                New password
              </label>
              <input v-model="pass1" :type="showPwd ? 'text' : 'password'" minlength="6" required class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                       text-zinc-900 placeholder-zinc-400 outline-none transition
                       focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                       dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                       caret-sky-500 selection:bg-sky-500/15" />
            </div>
            <div>
              <label class="block text-xs font-medium tracking-wide text-zinc-700 dark:text-zinc-300 mb-1.5">
                Confirm password
              </label>
              <input v-model="pass2" :type="showPwd ? 'text' : 'password'" minlength="6" required class="block w-full rounded-xl border border-zinc-200 bg-zinc-50/80 px-3.5 py-2.5 text-sm
                       text-zinc-900 placeholder-zinc-400 outline-none transition
                       focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:border-sky-500
                       dark:border-zinc-700 dark:bg-zinc-900/70 dark:text-zinc-100 dark:placeholder-zinc-500
                       caret-sky-500 selection:bg-sky-500/15" />
            </div>

            <label class="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
              <input v-model="showPwd" type="checkbox"
                class="h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-600" />
              Show password
            </label>

            <div v-if="info" class="rounded-xl border px-3 py-2 text-xs sm:text-sm"
              :class="isDark ? 'border-emerald-900/50 bg-emerald-950/60 text-emerald-300' : 'border-emerald-200/80 bg-emerald-50/90 text-emerald-700'">
              {{ info }}
            </div>

            <div v-if="error" class="rounded-xl border px-3 py-2 text-xs sm:text-sm"
              :class="isDark ? 'border-red-900/50 bg-red-950/60 text-red-300' : 'border-red-200/80 bg-red-50/90 text-red-700'">
              {{ error }}
            </div>

            <div class="flex items-center gap-2">
              <button type="submit" :disabled="loading || !canReset" class="flex-1 inline-flex items-center justify-center rounded-full px-4 py-2.5
                       text-sm font-semibold text-white shadow-lg shadow-sky-500/30
                       bg-sky-600 hover:bg-sky-700 active:scale-[.98]
                       disabled:opacity-60 disabled:cursor-not-allowed">
                {{ loading ? 'Saving…' : 'Save new password' }}
              </button>
              <button type="button" @click="mode = 'login'; error = ''; info = ''" class="rounded-full px-4 py-2.5 text-sm font-semibold
                       text-zinc-700 hover:bg-zinc-100
                       dark:text-zinc-300 dark:hover:bg-zinc-800/80">
                Back
              </button>
            </div>
          </form>

          <template v-if="mode === 'login' || mode === 'signup'">
            <div class="relative mt-3">
              <div class="absolute inset-0 flex items-center" aria-hidden="true">
                <div class="w-full border-t border-zinc-200 dark:border-zinc-800"></div>
              </div>
              <div class="relative flex justify-center text-xs">
                <span class="bg-white px-2 text-zinc-500 dark:bg-zinc-950 dark:text-zinc-400">
                  Or continue with
                </span>
              </div>
            </div>
            <div class="flex justify-center mt-1">
              <GoogleAuthButton :remember="true" size="large" @success="onAuthSuccess" @error="onSocialError" />
            </div>
          </template>
        </div>

        <div v-if="mode === 'login' || mode === 'signup'"
          class="mt-6 pt-4 border-t border-zinc-200 dark:border-zinc-800 text-center text-xs sm:text-sm">
          <span :class="isDark ? 'text-zinc-400' : 'text-zinc-500'">
            {{ mode === 'login' ? "Don't have an account?" : 'Already have an account?' }}
          </span>
          <button @click="toggleMode" type="button"
            class="ml-1 font-semibold text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300">
            {{ mode === 'login' ? 'Sign up' : 'Sign in' }}
          </button>
        </div>
      </div>

      <div class="hidden md:flex relative items-stretch overflow-hidden">
        <div class="absolute inset-0">
          <div class="absolute inset-0 bg-gradient-to-br from-sky-500 via-indigo-600 to-blue-800" />
          <div class="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-cyan-300/50 blur-3xl" />
          <div class="absolute -left-10 bottom-0 w-72 h-72 rounded-full bg-fuchsia-400/50 blur-3xl" />
        </div>

        <div class="relative flex-1 p-8 flex flex-col justify-between text-white">
          <div class="space-y-4">
            <p class="text-xs font-medium uppercase tracking-[0.25em] text-white/70">
              Palette overview
            </p>
            <h2 class="text-2xl font-semibold leading-tight">
              Build color systems that stay consistent across every surface.
            </h2>
            <p class="text-sm text-white/75 max-w-xs">
              Save palettes, tokens and themes that sync with your NexGridCo workspace.
            </p>
          </div>

          <div class="mt-6 grid grid-cols-2 gap-4">
            <div v-for="p in previewPalettes" :key="p.name"
              class="rounded-2xl bg-white/10 backdrop-blur-xl border border-white/15 p-4 space-y-3">
              <div class="flex items-center justify-between text-[11px]">
                <span class="font-semibold tracking-wide">{{ p.name }}</span>
                <span class="px-2 py-0.5 rounded-full bg-white/10 text-[10px] uppercase tracking-[0.16em]">
                  {{ p.tag }}
                </span>
              </div>
              <div class="flex gap-1.5">
                <div v-for="c in p.colors" :key="c" class="flex-1 h-8 rounded-lg border border-white/20"
                  :style="{ backgroundColor: c }"></div>
              </div>
              <p class="text-[10px] font-mono text-white/70 truncate">
                {{ p.colors.join(' · ') }}
              </p>
            </div>
          </div>

          <div class="mt-4 flex items-center justify-between text-[11px] text-white/65">
            <p>Accessible contrast presets included</p>
            <div class="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 border border-white/20">
              <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
              <span class="tracking-[0.16em] uppercase">Live sync</span>
            </div>
          </div>
        </div>
      </div>

      <button type="button" @click="closeModal" class="absolute right-3.5 top-3.5 h-9 w-9 grid place-items-center rounded-full
               bg-white/80 text-zinc-500 hover:bg-white shadow-sm
               dark:bg-zinc-900/80 dark:text-zinc-300 dark:hover:bg-zinc-800" aria-label="Close">
        <span class="material-symbols-outlined text-[20px]">close</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, defineEmits } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import GoogleAuthButton from '@/components/ui/GoogleAuthButton.vue'
import AuthService from '@/service/authService'

const emit = defineEmits(['close', 'auth-success'])
const route = useRoute()
const router = useRouter()

const mode = ref('login')
const name = ref('')
const email = ref('')
const password = ref('')
const pass1 = ref('')
const pass2 = ref('')
const token = ref('')

const loading = ref(false)
const error = ref('')
const info = ref('')
const showPwd = ref(false)
const isDark = ref(false)

const previewPalettes = [
  {
    name: 'Aurora Interface',
    tag: 'Product',
    colors: ['#0F172A', '#1D4ED8', '#22C55E', '#FACC15']
  },
  {
    name: 'Nordic Frost',
    tag: 'SaaS',
    colors: ['#020617', '#1E293B', '#3B82F6', '#E0F2FE']
  },
  {
    name: 'Sunrise Deck',
    tag: 'Marketing',
    colors: ['#020617', '#F97316', '#EC4899', '#A855F7']
  },
  {
    name: 'Neutral Grid',
    tag: 'System',
    colors: ['#020617', '#111827', '#E5E7EB', '#F9FAFB']
  }
]

const title = computed(() =>
  ({
    login: 'Welcome back',
    signup: 'Create your workspace',
    forgot: 'Reset your password',
    reset: 'Set a new password'
  })[mode.value]
)

const subtitle = computed(() =>
  ({
    login: 'Sign in to access palettes, tokens and color systems.',
    signup: 'Start building modern color systems with NexGridCo.',
    forgot: 'Enter your email and we will send you a reset link.',
    reset: 'Choose a strong password and confirm it below.'
  })[mode.value]
)

const canReset = computed(
  () => !!token.value && pass1.value.length >= 6 && pass1.value === pass2.value
)

function toggleMode() {
  mode.value = mode.value === 'login' ? 'signup' : 'login'
  error.value = ''
  info.value = ''
}

async function submitLogin() {
  loading.value = true
  error.value = ''
  info.value = ''
  try {
    const data = await AuthService.login({ email: email.value, password: password.value })
    AuthService.handleLoginSuccess(data)
    emit('auth-success', data)
  } catch (e) {
    error.value = e?.response?.data?.message || 'Invalid email or password.'
  } finally {
    loading.value = false
  }
}

async function submitSignup() {
  loading.value = true
  error.value = ''
  info.value = ''
  try {
    const data = await AuthService.signup({ name: name.value, email: email.value, password: password.value })
    AuthService.handleLoginSuccess(data)
    emit('auth-success', data)
  } catch (e) {
    error.value = e?.response?.data?.message || 'Could not create your account.'
  } finally {
    loading.value = false
  }
}

async function submitForgot() {
  loading.value = true
  error.value = ''
  info.value = ''
  try {
    await AuthService.requestPasswordReset(email.value)
    info.value = 'If the email exists, we sent a reset link.'
  } catch {
    info.value = 'If the email exists, we sent a reset link.'
  } finally {
    loading.value = false
  }
}

async function submitReset() {
  if (!canReset.value) return
  loading.value = true
  error.value = ''
  info.value = ''
  try {
    await AuthService.resetPassword({ token: token.value, password: pass1.value })
    info.value = 'Password updated. You can sign in now.'
    setTimeout(() => {
      token.value = ''
      pass1.value = ''
      pass2.value = ''
      mode.value = 'login'
      const q = { ...route.query, auth: '1' }
      delete q.token
      router.replace({ path: route.path, query: q, hash: route.hash })
      info.value = ''
    }, 1200)
  } catch (e) {
    error.value = e?.response?.data?.message || 'Reset link is invalid or expired.'
  } finally {
    loading.value = false
  }
}

function onAuthSuccess(data) {
  AuthService.handleLoginSuccess(data)
  emit('auth-success', data)
}

function onSocialError(msg) {
  error.value = msg || 'Failed to sign in with Google.'
}

function closeModal() {
  const q = { ...route.query }
  delete q.auth
  delete q.token
  router.replace({ path: route.path, query: q, hash: route.hash })
  emit('close')
}

function onKey(e) {
  if (e.key === 'Escape') closeModal()
}

function applyModeFromQuery() {
  error.value = ''
  info.value = ''
  const a = String(route.query.auth || '')
  const t = String(route.query.token || '')
  if (a === 'reset' && t) {
    mode.value = 'reset'
    token.value = t
  } else if (a === '1') {
    mode.value = 'login'
  }
}

let themeObserver
onMounted(() => {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href =
    'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght@400;600&display=swap'
  document.head.appendChild(link)

  isDark.value = document.documentElement.classList.contains('dark')
  document.body.classList.add('overflow-hidden')
  window.addEventListener('keydown', onKey)

  themeObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

  applyModeFromQuery()
})

watch(
  () => route.query,
  () => applyModeFromQuery()
)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  document.body.classList.remove('overflow-hidden')
  themeObserver?.disconnect?.()
})
</script>

<style scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
}
</style>
